import { useState } from "react";
import { imageTypes } from "../constants/lists";

export const usePostImageUpload = ({ onUpload, onError, maxSize = 10 * 1024 * 1024 }) => {
  const [cropperOpen, setCropperOpen] = useState(false);
  const [tempImageSrc, setTempImageSrc] = useState(null);
  const [tempFile, setTempFile] = useState(null);
  const [error, setError] = useState("");

  const convertSvgToRaster = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Preserve original SVG dimensions or use reasonable defaults
          const width = img.width || 400;
          const height = img.height || 400;
          canvas.width = width;
          canvas.height = height;
          
          // Fill with white background
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, width, height);
          
          // Draw the SVG image
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            if (blob) {
              const rasterFile = new File([blob], file.name.replace('.svg', '.png'), {
                type: 'image/png'
              });
              resolve(rasterFile);
            } else {
              reject(new Error('Failed to convert SVG'));
            }
          }, 'image/png');
        };
        img.onerror = () => reject(new Error('Failed to load SVG'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleFile = async (file) => {
    if (!file) {
      return;
    }

    if (file.size > maxSize) {
      setError("File size is over the 10MB limit. Please compress the file or upload a smaller file.");
      return;
    }

    if (!imageTypes.includes(file.type)) {
      setError("Please upload a valid image file.\n(jpeg, jpg, png, svg+xml)");
      return;
    }

    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file.");
      return;
    }

    setError("");
    
    // Convert SVG to raster format for better compatibility
    if (file.type === 'image/svg+xml') {
      try {
        const rasterFile = await convertSvgToRaster(file);
        setTempFile(rasterFile);
        setTempImageSrc(URL.createObjectURL(rasterFile));
      } catch (error) {
        console.error('SVG conversion failed:', error);
        setError('Failed to process SVG file. Please try a different format.');
        return;
      }
    } else {
      setTempFile(file);
      setTempImageSrc(URL.createObjectURL(file));
    }
    
    setCropperOpen(true);
  };

  const handleFileInput = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFile(file);
      event.target.value = '';
    }
  };

  const handleCroppedImage = (croppedFile) => {
    onUpload?.(croppedFile);
    resetUpload();
  };

  const handleSkipCrop = () => {
    if (tempFile) {
      // For post images, use original file without processing
      onUpload?.(tempFile);
    }
    resetUpload();
  };

  const handleCancel = () => {
    resetUpload();
  };

  const resetUpload = () => {
    setError("");
    setCropperOpen(false);
    setTempImageSrc(null);
    setTempFile(null);
  };

  return {
    cropperOpen,
    setCropperOpen,
    tempImageSrc,
    error,
    setError,
    handleFileInput,
    handleCroppedImage,
    handleSkipCrop,
    handleCancel,
    resetUpload,
  };
};